import React, { useState, useEffect, useRef } from 'react';
import { Camera, Heart, Share2, X, ImagePlus, Sparkles, Moon, Sun, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const USER_ID = 'user_' + Math.random().toString(36).substr(2, 9);

export default function Vistagram() {
  const [posts, setPosts] = useState([]);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [caption, setCaption] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [likeAnimation, setLikeAnimation] = useState({});
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [cameraFacing, setCameraFacing] = useState('user'); // 'user' for front, 'environment' for back
  
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!Array.isArray(posts)) {
      setPosts([]);
    }
  }, [posts]);

  useEffect(() => {
    fetchPosts();
    // Check for saved theme preference
    const savedTheme = localStorage.getItem('vistagram-theme');
    if (savedTheme === 'dark') {
      setIsDarkMode(true);
    }
  }, []);

  useEffect(() => {
    // Save theme preference
    localStorage.setItem('vistagram-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/posts`);
      const data = await response.json();
      setPosts(data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: cameraFacing } // Use the selected camera facing mode
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      streamRef.current = stream;
      setShowCamera(true);
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Could not access camera. Please check permissions or use file upload.');
    }
  };

  const switchCamera = async () => {
    const newFacing = cameraFacing === 'user' ? 'environment' : 'user';
    setCameraFacing(newFacing);
    
    if (showCamera) {
      stopCamera();
      // Small delay to ensure camera is properly stopped before restarting
      setTimeout(async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ 
            video: { facingMode: newFacing }
          });
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
          streamRef.current = stream;
          setShowCamera(true);
        } catch (error) {
          console.error('Error switching camera:', error);
          alert('Could not switch camera.');
        }
      }, 100);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
  };

  const captureImage = () => {
    const canvas = document.createElement('canvas');
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const imageData = canvas.toDataURL('image/jpeg');
    setCapturedImage(imageData);
    stopCamera();
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCapturedImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const dataURLtoBlob = (dataURL) => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const uploadPost = async () => {
    if (!capturedImage || !caption || !username) {
      alert('Please provide username, caption, and an image');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      const imageBlob = dataURLtoBlob(capturedImage);
      formData.append('image', imageBlob, 'capture.jpg');
      formData.append('username', username);
      formData.append('caption', caption);

      const response = await fetch(`${API_URL}/posts`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        setCapturedImage(null);
        setCaption('');
        fetchPosts();
        alert('Post uploaded successfully! ✨');
      }
    } catch (error) {
      console.error('Error uploading post:', error);
      alert('Failed to upload post');
    } finally {
      setLoading(false);
    }
  };

  const toggleLike = async (postId) => {
    try {
      setLikeAnimation(prev => ({ ...prev, [postId]: true }));
      setTimeout(() => {
        setLikeAnimation(prev => ({ ...prev, [postId]: false }));
      }, 600);

      const response = await fetch(`${API_URL}/posts/${postId}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: USER_ID }),
      });

      if (response.ok) {
        const updatedPost = await response.json();
        setPosts(posts.map(p => p.id === postId ? updatedPost : p));
      }
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const sharePost = async (postId) => {
    try {
      await fetch(`${API_URL}/posts/${postId}/share`, {
        method: 'POST',
      });

      const shareLink = `${window.location.origin}/post/${postId}`;
      setShareUrl(shareLink);
      setShowShareModal(true);
      
      setPosts(posts.map(p => 
        p.id === postId ? { ...p, shares: p.shares + 1 } : p
      ));
    } catch (error) {
      console.error('Error sharing post:', error);
    }
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(shareUrl);
    alert('Link copied to clipboard! 📋');
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    return 'Just now';
  };

  // Theme-based class names
  const bgClass = isDarkMode 
    ? 'bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900' 
    : 'bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100';
  
  const cardClass = isDarkMode 
    ? 'bg-gray-800/90 border-gray-700/50' 
    : 'bg-white/90 border-white/20';
  
  const textClass = isDarkMode ? 'text-gray-100' : 'text-gray-800';
  const textSecondaryClass = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const headerClass = isDarkMode 
    ? 'backdrop-blur-xl bg-gray-900/70 border-gray-800/50' 
    : 'backdrop-blur-xl bg-white/70 border-white/20';

  return (
    <div className={`min-h-screen relative overflow-hidden transition-colors duration-500 ${bgClass}`}>
      {/* Animated Gradient Background */}
      <div className="fixed inset-0 -z-10">
        {isDarkMode ? (
          <>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(139,92,246,0.15),transparent_50%)] animate-pulse"></div>
            <div className="absolute top-0 left-0 w-96 h-96 bg-purple-900 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
            <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-900 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
            <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-pink-900 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
          </>
        ) : (
          <>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(168,85,247,0.1),transparent_50%)] animate-pulse"></div>
            <div className="absolute top-0 left-0 w-96 h-96 bg-indigo-300 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob"></div>
            <div className="absolute top-0 right-0 w-96 h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob animation-delay-2000"></div>
            <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob animation-delay-4000"></div>
          </>
        )}
      </div>

      {/* Header with Glass Effect */}
      <header className={`sticky top-0 z-10 ${headerClass} border-b shadow-lg transition-colors duration-500`}>
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className={`${isDarkMode ? 'text-purple-400' : 'text-purple-600'} animate-pulse`} size={28} />
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 bg-clip-text text-transparent animate-gradient">
              Vistagram
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className={`p-2.5 rounded-full transition-all duration-300 hover:scale-110 ${
                isDarkMode 
                  ? 'bg-gray-700 hover:bg-gray-600 text-yellow-400' 
                  : 'bg-purple-100 hover:bg-purple-200 text-purple-600'
              }`}
              aria-label="Toggle theme"
            >
              {isDarkMode ? (
                <Sun size={22} className="animate-spin-slow" />
              ) : (
                <Moon size={22} />
              )}
            </button>
            <button
              onClick={startCamera}
              className="group flex items-center gap-2 bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 text-white px-5 py-2.5 rounded-full hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <Camera size={20} className="group-hover:rotate-12 transition-transform duration-300" />
              <span className="font-semibold">New Post</span>
            </button>
          </div>
        </div>
      </header>

      <style jsx>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s ease infinite;
        }
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
        @keyframes heartPop {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.3); }
        }
        .heart-pop {
          animation: heartPop 0.6s ease;
        }
      `}</style>

      {/* Camera Modal with Animation */}
      {showCamera && (
        <div className="fixed inset-0 bg-black/95 z-50 flex flex-col animate-fadeIn">
          <div className="flex justify-between items-center p-4">
            <button 
              onClick={switchCamera}
              className="flex items-center gap-2 text-white bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full hover:bg-white/20 transition-all duration-300"
            >
              <RefreshCw size={20} />
              <span className="text-sm font-medium">
                {cameraFacing === 'user' ? 'Switch to Back' : 'Switch to Front'}
              </span>
            </button>
            <button 
              onClick={stopCamera} 
              className="text-white hover:rotate-90 transition-transform duration-300"
            >
              <X size={32} />
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="max-w-full max-h-full rounded-lg shadow-2xl"
              style={{ transform: cameraFacing === 'user' ? 'scaleX(-1)' : 'scaleX(1)' }}
              data-testid="video-preview"
            />
          </div>
          <div className="p-6 flex justify-center">
            <button
              onClick={captureImage}
              className="w-16 h-16 bg-white rounded-full border-4 border-purple-500 hover:border-pink-500 transition-all duration-300 hover:scale-110 shadow-lg hover:shadow-2xl"
            />
          </div>
        </div>
      )}

      {/* Upload Form with Animation */}
      {capturedImage && (
        <div className="max-w-2xl mx-auto p-4 animate-slideUp">
          <div className={`${cardClass} backdrop-blur-lg rounded-2xl shadow-2xl p-6 border transition-colors duration-500`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className={`text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent`}>
                Create Post ✨
              </h2>
              <button
                onClick={() => setCapturedImage(null)}
                className={`${textSecondaryClass} hover:${textClass} hover:rotate-90 transition-all duration-300`}
              >
                <X size={24} />
              </button>
            </div>
            
            <img
              src={capturedImage}
              alt="Captured"
              className="w-full rounded-xl mb-4 shadow-lg hover:shadow-2xl transition-shadow duration-300"
              data-testid="image-preview"
            />
            
            <input
              type="text"
              placeholder="Your username ✨"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              data-testid="username-input"
              className={`w-full px-4 py-3 border-2 rounded-xl mb-3 focus:outline-none focus:ring-2 transition-all duration-300 ${
                isDarkMode 
                  ? 'border-gray-600 bg-gray-700/50 text-white placeholder-gray-400 focus:border-purple-500 focus:ring-purple-500/50' 
                  : 'border-purple-200 bg-white/50 text-gray-800 focus:border-purple-600 focus:ring-purple-200'
              }`}
            />
            
            <textarea
              placeholder="Write a caption... 💭"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={3}
              data-testid="caption-input"
              className={`w-full px-4 py-3 border-2 rounded-xl mb-4 focus:outline-none focus:ring-2 transition-all duration-300 resize-none ${
                isDarkMode 
                  ? 'border-gray-600 bg-gray-700/50 text-white placeholder-gray-400 focus:border-purple-500 focus:ring-purple-500/50' 
                  : 'border-purple-200 bg-white/50 text-gray-800 focus:border-purple-600 focus:ring-purple-200'
              }`}
            />
            
            <button
              onClick={uploadPost}
              disabled={loading}
              data-testid="share-post-button"
              className="w-full bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 text-white py-4 rounded-xl font-bold hover:scale-105 transition-all duration-300 disabled:opacity-50 shadow-lg hover:shadow-xl"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Uploading...
                </span>
              ) : (
                'Share Post ✨'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Alternative Upload Button */}
      {!capturedImage && (
        <div className="max-w-2xl mx-auto p-4">
          <div className={`${cardClass} backdrop-blur-lg rounded-2xl shadow-lg p-8 flex items-center justify-center border hover:shadow-2xl transition-all duration-300 hover:scale-105`}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
              data-testid="file-input"
            />
            <button
              onClick={() => fileInputRef.current.click()}
              className={`flex items-center gap-3 transition-colors duration-300 ${
                isDarkMode 
                  ? 'text-purple-400 hover:text-pink-400' 
                  : 'text-purple-600 hover:text-pink-600'
              }`}
            >
              <ImagePlus size={28} className="animate-bounce" />
              <span className="font-bold text-lg">Or upload from device 📱</span>
            </button>
          </div>
        </div>
      )}

      {/* Timeline with Stagger Animation */}
      <div className="max-w-2xl mx-auto p-4 space-y-6">
        {posts.map((post, index) => (
          <div 
            key={post.id} 
            className={`${cardClass} backdrop-blur-lg rounded-2xl shadow-xl overflow-hidden border hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] animate-slideUp`}
            style={{ animationDelay: `${index * 100}ms` }}
          >
            {/* Post Header */}
            <div className="p-4 flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-600 via-pink-600 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg hover:scale-110 transition-transform duration-300">
                {post.username[0].toUpperCase()}
              </div>
              <div>
                <p className={`font-bold ${textClass}`}>{post.username}</p>
                <p className={`text-sm ${textSecondaryClass}`}>{formatTimestamp(post.timestamp)}</p>
              </div>
            </div>

            {/* Post Image */}
            <img
              src={post.imageUrl}
              alt={post.caption}
              className="w-full object-cover max-h-96 hover:scale-105 transition-transform duration-500"
            />

            {/* Post Actions */}
            <div className="p-4">
              <div className="flex items-center gap-6 mb-3">
                <button
                  onClick={() => toggleLike(post.id)}
                  className="flex items-center gap-2 hover:scale-110 transition-all duration-300 group"
                  data-testid={`like-button-${post.id}`}
                >
                  <Heart
                    size={28}
                    className={`transition-all duration-300 ${
                      post.likedBy?.includes(USER_ID) 
                        ? 'fill-red-500 text-red-500' 
                        : `${isDarkMode ? 'text-gray-300' : 'text-gray-700'} group-hover:text-red-500`
                    } ${likeAnimation[post.id] ? 'heart-pop' : ''}`}
                  />
                  <span className={`font-bold text-lg ${textClass}`}>{post.likes}</span>
                </button>
                <button
                  onClick={() => sharePost(post.id)}
                  className="flex items-center gap-2 hover:scale-110 transition-all duration-300 group"
                  data-testid={`share-button-${post.id}`}
                >
                  <Share2 
                    size={28} 
                    className={`transition-colors duration-300 ${
                      isDarkMode 
                        ? 'text-gray-300 group-hover:text-blue-400' 
                        : 'text-gray-700 group-hover:text-blue-500'
                    }`}
                  />
                  <span className={`font-bold text-lg ${textClass}`}>{post.shares}</span>
                </button>
              </div>

              {/* Caption */}
              <p className={`${textClass} leading-relaxed`}>
                <span className="font-bold">{post.username}</span>{' '}
                {post.caption}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Share Modal with Animation */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className={`${cardClass} backdrop-blur-xl rounded-2xl p-8 max-w-md w-full shadow-2xl border animate-scaleIn transition-colors duration-500`}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Share Post 🚀
              </h3>
              <button
                onClick={() => setShowShareModal(false)}
                className={`${textSecondaryClass} hover:${textClass} hover:rotate-90 transition-all duration-300`}
              >
                <X size={24} />
              </button>
            </div>
            <div className={`p-4 rounded-xl mb-6 break-all font-mono text-sm shadow-inner ${
              isDarkMode 
                ? 'bg-gray-700 text-gray-200' 
                : 'bg-gradient-to-r from-purple-100 to-pink-100'
            }`}>
              {shareUrl}
            </div>
            <button
              onClick={copyShareLink}
              className="w-full bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 text-white py-4 rounded-xl font-bold hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Copy Link 📋
            </button>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.5s ease-out forwards;
        }
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}